import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import CostGrid from '../components/CostGrid';
import JsonImportExport from '../components/JsonImportExport';

const TABS = [
  { id: 'mech', label: 'Mech/Weapon Costs', file: 'mech_upgrade_costs.json' },
  { id: 'pilot', label: 'Pilot Costs', file: 'pilot_upgrade_costs.json' },
  { id: 'mod', label: 'Mod Costs', file: 'mod_cost.json' },
];

const AdminUpgradeCostsPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { post } = useAdminApi();
  const [activeTab, setActiveTab] = useState('mech');
  const [data, setData] = useState({ mech: [], pilot: [], mod: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [mechRes, pilotRes, modRes] = await Promise.all([
        fetch('/mech_upgrade_costs.json'),
        fetch('/pilot_upgrade_costs.json'),
        fetch('/mod_cost.json'),
      ]);
      setData({
        mech: await mechRes.json(),
        pilot: await pilotRes.json(),
        mod: await modRes.json(),
      });
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (updatedData) => {
    const tab = TABS.find(t => t.id === activeTab);
    setSaving(true);
    try {
      await post('/admin-update-data.php', { file: tab.file, action: 'update', data: updatedData });
      setData(prev => ({ ...prev, [activeTab]: updatedData }));
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (imported) => {
    const tab = TABS.find(t => t.id === activeTab);
    try {
      await post('/admin-update-data.php', { file: tab.file, action: 'update', data: imported });
      setShowImportExport(false);
      await loadData();
    } catch (err) { alert('Import failed: ' + err.message); }
  };

  // Extract cost keys dynamically from first row of data
  const getCostKeys = (tabData) => {
    if (!tabData || tabData.length === 0) return [];
    const row = tabData[0];
    return Object.keys(row).filter(k => k !== 'star' && k !== 'level');
  };

  // Extract value fields from first cost key
  const getValueFields = (tabData) => {
    if (!tabData || tabData.length === 0) return [];
    const costKeys = getCostKeys(tabData);
    if (costKeys.length === 0) return [];
    const sample = tabData[0][costKeys[0]];
    return sample ? Object.keys(sample) : [];
  };

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  const currentData = data[activeTab] || [];
  const costKeys = getCostKeys(currentData);
  const valueFields = getValueFields(currentData);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Upgrade Costs</h1>
        <button onClick={() => setShowImportExport(true)}
          className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
          Import/Export
        </button>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${dark ? 'border-[#333]' : 'border-gray-200'}`}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? dark
                  ? 'border-[#FF9800] text-[#FF9800]'
                  : 'border-indigo-600 text-indigo-600'
                : dark
                  ? 'border-transparent text-gray-500 hover:text-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {costKeys.length > 0 ? (
        <CostGrid
          key={activeTab}
          data={currentData}
          costKeys={costKeys}
          valueFields={valueFields}
          onSave={handleSave}
          saving={saving}
        />
      ) : (
        <div className={`text-sm ${dark ? 'text-gray-500' : 'text-gray-400'}`}>No data available for this tab.</div>
      )}

      <JsonImportExport
        open={showImportExport}
        title={TABS.find(t => t.id === activeTab)?.label}
        data={currentData}
        onImport={handleImport}
        onClose={() => setShowImportExport(false)}
      />
    </div>
  );
};

export default AdminUpgradeCostsPage;
